import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  ActivateDeactivateCommands,
  CirculationPumpStatusProperties,
  DcSensorValueProperties,
  DeviceConnectionStatus,
  DeviceIds,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  GatewayType,
  HeatingConfigurationRegulationProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  MatIconType,
  OptimizationRecommendationsType,
  RenameHeatingCircuitCommands,
  RenameHeatingCircuitProperties,
  RoomChannelPillInfo,
  RoomChannelsEntityProperties,
  RoomProperties,
  RoomsStatusProperties,
  ZoneDemandProperties,
} from '@vi-iot/client';

import {
  InstallationFeaturesStore,
  InstallationDetailsStore,
  isReady,
  ViguideRecommendationsStore,
  OptimizationRecommendationSlug,
  DevicesStore,
} from '@store';

import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { CustomTranslationsService, STATE } from '../../../operational-data-common/translations';

import { PillType } from '../../../shared/components/pill/pill.model';
import { EditHeatingCircuitNameModalComponent } from './edit-heating-circuit-name-modal/edit-heating-circuit-name-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { NO_VALUE } from '@pipes';
import { InstallationService } from '../../services/installation.service';

@Component({
  selector: 'app-heating-circuit-features-panel',
  templateUrl: './heating-circuit-features-panel.component.html',
  styleUrls: ['./heating-circuit-features-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeatingCircuitFeaturesPanelComponent implements OnInit, OnDestroy {
  @Input() circuitId: string;
  @Input() header: string;
  @Input() deviceSelectId: string;
  @Input() deviceId: string;

  readonly pillType = PillType;
  readonly hcPrefix = 'hc';
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly NO_VALUE = NO_VALUE;

  STATE = STATE;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  gatewayType = GatewayType;
  heatingCircuitNameFeature$: Observable<
    Feature<RenameHeatingCircuitProperties, RenameHeatingCircuitCommands>
  >;
  operatingProgram$: Observable<Feature<IsStringValueProperty, unknown>>; // TODO check it - not all programs has temperature key
  operatingModesActive$: Observable<Feature<IsStringValueProperty, never>>;
  heatingRegulation$: Observable<Feature<HeatingConfigurationRegulationProperties, never>>;
  targetTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  flowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  operatingProgramsActive$: Observable<Feature<IsStringValueProperty, never>>;
  heatingZone$: Observable<Feature<IsActiveProperty, never>>;
  heatingZoneDemand$: Observable<Feature<ZoneDemandProperties, never>>;
  status$: Observable<Feature<RoomsStatusProperties, ActivateDeactivateCommands>>;
  isHeatingCircuitWithActiveSRCAndDevices$: Observable<boolean>;
  vcsOptimizationData$: Observable<RoomChannelPillInfo>;
  defaultVCSOData = {
    type: PillType.secondary,
    message: this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_INACTIVE'
    ),
    tooltip: this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.OPTIMIZATION_INACTIVE_MESSAGE'
    ),
    alertType: MatIconType.Info,
  };

  isTargetFlowTemperatureVisible$: Observable<boolean>;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    protected customTranslationsService: CustomTranslationsService,
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private dialog: MatDialog,
    private viguideRecommendationsStore: ViguideRecommendationsStore,
    private translateService: TranslateService,
    private installationService: InstallationService
  ) {}

  ngOnInit(): void {
    this.operatingModesActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsStringValueProperty,
      never
    >(FeatureNames.circuitActiveMode(this.circuitId), this.deviceId);

    this.heatingCircuitNameFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitName(this.circuitId),
      this.deviceId
    );

    this.heatingRegulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationRegulation,
      this.deviceId
    );

    this.flowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSupplyTemperature(this.circuitId),
      this.deviceId
    );

    this.targetTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      never
    >(FeatureNames.circuitTemperature(this.circuitId), this.deviceId);

    this.operatingProgramsActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsStringValueProperty,
      never
    >(FeatureNames.circuitActiveProgram(this.circuitId), this.deviceId);

    this.heatingZone$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingZoneMode(this.circuitId),
      this.deviceId
    );

    this.heatingZoneDemand$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitHeatingZoneDemand(this.circuitId),
      this.deviceId
    );

    this.isHeatingCircuitWithActiveSRCAndDevices$ = this.devicesStore.isAnySRCDeviceAssignedToHeatingCircuit(
      Number(this.circuitId),
      DeviceIds.RoomControl
    );

    this.vcsOptimizationData$ = this.installationFeaturesStore
      .selectRoomChannelByHeatingCircuitId(this.circuitId)
      .pipe(
        filter(Boolean),
        switchMap((channelInfo: Feature<RoomChannelsEntityProperties, never>) =>
          combineLatest([
            this.installationFeaturesStore.selectRoomsAssignedToChannel(
              channelInfo?.properties?.id?.value
            ),
            this.installationFeaturesStore.selectDeviceStatusInChannel(
              channelInfo?.properties?.id?.value
            ),
            this.installationFeaturesStore.selectIsAtleastOneDeviceOnlineInChannel(
              channelInfo?.properties?.id?.value
            ),
          ]).pipe(
            map(([roomsInChannel, devicesInChannel, isAtleastOneDeviceOnlineInChannel]) => ({
              channelInfo,
              roomsInChannel,
              devicesInChannel,
              isAtleastOneDeviceOnlineInChannel,
            }))
          )
        ),
        map((value) => this.getVCSOptimizationData(value)),
        startWith(this.defaultVCSOData)
      );

    this.initActiveProgramSubscription();

    this.isTargetFlowTemperatureVisible$ = this.installationService.isHeatingCircuitTargetFlowTemperatureVisible(
      this.circuitId,
      this.deviceId
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  getPumpStatus(): Observable<Feature<CirculationPumpStatusProperties, never>> {
    return this.circuitId
      ? this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.circuitCirculationPump(this.circuitId),
          this.deviceId
        )
      : of(null);
  }

  openRenameHeatingCircuitModal(): void {
    this.dialog.open(EditHeatingCircuitNameModalComponent, {
      data: {
        name: this.header,
        circuitId: this.circuitId,
      },
    });
  }

  getHCRecommendations(circuitId: string): Observable<string> {
    const hcSlug = this.getHCOptimizationSlug(circuitId);
    const optimizationsTypes = Object.values(OptimizationRecommendationsType).filter((type) =>
      type.includes(hcSlug)
    );

    return this.viguideRecommendationsStore
      .getAvailableRecommendationsForTile(optimizationsTypes, hcSlug)
      .pipe(
        map((recommendations) =>
          recommendations.map((recommendation) => recommendation.replace(hcSlug, ''))
        ),
        map((recommendations) =>
          recommendations.map((recommendation) =>
            this.translateService.instant(
              this.customTranslationsService.OPTIMIZATION_RECOMMENDATIONS[recommendation]
            )
          )
        ),
        map((recommendations) => recommendations.join(', '))
      );
  }

  getHCOptimizationSlug(circuitId: string): OptimizationRecommendationSlug {
    return OptimizationRecommendationSlug[this.hcPrefix + circuitId];
  }

  private initActiveProgramSubscription(): void {
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomStatus,
      DeviceIds.RoomControl
    );
    this.operatingProgramsActive$
      .pipe(
        withLatestFrom(this.status$),
        filter(
          ([feature, status]) =>
            isReady(feature) &&
            !!feature?.properties?.value?.value &&
            !status?.properties?.active?.value
        ),
        map(([feature]) => feature.properties.value.value),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((activeProgram) => {
        this.operatingProgram$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.circuitOperatingProgram(this.circuitId, activeProgram)
        );
      });
  }

  private getVCSOptimizationData(value: {
    roomsInChannel: Feature<RoomProperties, never>[];
    devicesInChannel: Feature<DeviceConnectionStatus, never>[];
    isAtleastOneDeviceOnlineInChannel: boolean;
    channelInfo: Feature<RoomChannelsEntityProperties, never>;
  }): RoomChannelPillInfo {
    const {
      roomsInChannel,
      devicesInChannel,
      isAtleastOneDeviceOnlineInChannel,
      channelInfo,
    } = value;
    const { roomInfluenceEnabled, active } = channelInfo?.properties || {};

    let vcsOptimizationStatusPillData = {
      type: PillType.secondary,
      message: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_INACTIVE'
      ),
      tooltip: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.OPTIMIZATION_INACTIVE_MESSAGE'
      ),
      alertType: MatIconType.Info,
    };

    if (roomInfluenceEnabled?.value && active?.value) {
      vcsOptimizationStatusPillData = {
        type: PillType.success,
        message: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_ACTIVE'
        ),
        tooltip: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.VCSO_ACTIVE_MESSAGE'
        ),
        alertType: MatIconType.Info,
      };
    }

    if (roomInfluenceEnabled?.value && !active?.value) {
      if (roomsInChannel?.length === 0) {
        vcsOptimizationStatusPillData = {
          type: PillType.mediumInverted,
          message: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_PAUSED'
          ),
          tooltip: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.NO_REFERENCE_ROOMS'
          ),
          alertType: MatIconType.Warning,
        };
      } else if (devicesInChannel?.length === 0) {
        vcsOptimizationStatusPillData = {
          type: PillType.mediumInverted,
          message: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_PAUSED'
          ),
          tooltip: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.NO_DEVICES_FOUND_IN_THE_CHANNEL'
          ),
          alertType: MatIconType.Warning,
        };
      } else if (!isAtleastOneDeviceOnlineInChannel) {
        vcsOptimizationStatusPillData = {
          type: PillType.mediumInverted,
          message: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_PAUSED'
          ),
          tooltip: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.DEVICE_INFORMATION_UNAVAILABLE'
          ),
          alertType: MatIconType.Warning,
        };
      }
    }

    if (roomInfluenceEnabled?.value === false) {
      vcsOptimizationStatusPillData = {
        type: PillType.secondary,
        message: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS.OPTIMIZATION_INACTIVE'
        ),
        tooltip: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.CIRCUIT.ROOM_CHANNELS_PILL.STATUS_INFO.OPTIMIZATION_INACTIVE_MESSAGE'
        ),
        alertType: MatIconType.Info,
      };
    }

    return vcsOptimizationStatusPillData;
  }
}
